import {
  Component,
  Element,
  h,
  Host,
  Listen,
  Prop,
  State,
} from "@stencil/core";
import { showDealerSearchDropdown } from "@phn/utility/helper/dealer-search-helper";
import { navContentStore } from "@phn/state/nav-content-store";
import { Dealer } from "../../../../shared/entities/content";
import { splitLocale } from "../../../../shared/entities/locale";
import { DealerSearchMapLink } from "../dealer-search-results/dealer-search-map-link";

type DealerSearchFormStrategy =
  | "geolocation"
  | "dropdown"
  | "byNumberOfDealers";

@Component({
  tag: "phn-nd-dealer-search-form",
  styleUrl: "dealer-search-form.scss",
  scoped: true,
  assetsDirs: ["./assets"],
})
export class DealerSearchForm {
  @Prop() dealer!: Dealer;

  @Prop() locale!: string;

  @Prop() showHeadline = true;

  @Prop() breakpoint!: number;

  @State() showOverlay = false;

  @State() formErrorMessage: string | null = null;

  @State() showSkeleton = true;

  @Listen("predictionsToggle")
  toggleOverlay(e: CustomEvent<boolean>): void {
    this.showOverlay = e.detail;
  }

  @State() animateResults = false;

  @State() loadingResults = false;

  @Element() el!: HTMLPhnNdDealerSearchFormElement;

  @Listen("removeSkeletonOnDealerSearchForm")
  loadedAutoCompleteListener(): void {
    this.showSkeleton = false;
  }

  private getDealerFormStrategy(): DealerSearchFormStrategy {
    const { dealer } = navContentStore.state;
    if (dealer.numberOfDealers <= 5) {
      return "byNumberOfDealers";
    }

    if (showDealerSearchDropdown(splitLocale(this.locale).country))
      return "dropdown";

    return "geolocation";
  }

  private setLoadingState(value: boolean): void {
    this.loadingResults = value;
  }

  private formComponent(): HTMLElement {
    const dealerSearchFormStrategies: Record<
      DealerSearchFormStrategy,
      () => HTMLElement
    > = {
      geolocation: () => (
        <phn-nd-dealer-search-form-radius
          locale={this.locale}
          onLoadingResults={(event) => this.setLoadingState(event.detail)}
        />
      ),
      dropdown: () => (
        <phn-nd-dealer-search-form-dropdown
          locale={this.locale}
          breakpoint={this.breakpoint}
          onLoadingResults={(event) => this.setLoadingState(event.detail)}
        />
      ),
      byNumberOfDealers: () => (
        <phn-nd-dealer-search-by-number-of-dealers
          locale={this.locale}
          onLoadingResults={(event) => this.setLoadingState(event.detail)}
        />
      ),
    };

    const formStrategy = this.getDealerFormStrategy();
    return dealerSearchFormStrategies[formStrategy]();
  }

  hasMapLink(): boolean {
    return !!this.dealer?.search?.map;
  }

  render(): HTMLPhnNdDealerSearchFormElement {
    return (
      <Host class={this.showSkeleton ? "skeleton" : ""}>
        {this.formComponent()}
        {this.hasMapLink() && (
          <div
            class={`map-link-container ${this.getDealerFormStrategy() === "byNumberOfDealers" ? "no-margin" : ""}`}
          >
            <DealerSearchMapLink />
          </div>
        )}

        {this.loadingResults && (
          <div class="results-spinner">
            <phn-p-spinner
              size="medium"
              aria={{ "aria-label": "Loading page content" }}
            />
          </div>
        )}

        <phn-nd-dealer-search-results
          locale={this.locale}
          hasMapLink={this.hasMapLink()}
          breakpoint={this.breakpoint}
          showOverlay={this.showOverlay}
          role="list"
        />
      </Host>
    );
  }
}
